// Converts pasted clipboard content (HTML from Word/Google Docs/web, or plain
// text) into the blog editor's markdown-ish format:
//   ## h2 | ### h3 | **bold** | • bullet | blank line between paragraphs.

const BOLD_TAGS = ['strong', 'b'];
const SKIP_TAGS = ['script', 'style', 'img', 'figure', 'iframe', 'svg', 'button', 'nav', 'footer', 'header', 'aside', 'form'];

const inlineText = (node) => {
  let out = '';
  node.childNodes.forEach((child) => {
    if (child.nodeType === Node.TEXT_NODE) {
      out += child.textContent.replace(/\u00a0/g, ' ');
      return;
    }
    if (child.nodeType !== Node.ELEMENT_NODE) return;
    const tag = child.tagName.toLowerCase();
    if (SKIP_TAGS.includes(tag)) return;
    if (tag === 'br') { out += '\n'; return; }
    if (tag === 'a') {
      const href = child.getAttribute('href') || '';
      const label = inlineText(child).replace(/\*\*/g, '').trim();
      if (label && (/^https?:\/\//i.test(href) || href.startsWith('/'))) {
        out += `[${label}](${href})`;
      } else {
        out += label;
      }
      return;
    }
    if (BOLD_TAGS.includes(tag) || (child.style && parseInt(child.style.fontWeight, 10) >= 600)) {
      const t = inlineText(child).trim();
      out += t ? `**${t}**` : '';
      return;
    }
    out += inlineText(child);
  });
  return out;
};

const cleanLine = (text) => text.replace(/\n+/g, ' ').replace(/\s+/g, ' ').trim();

const blockify = (node, blocks) => {
  node.childNodes.forEach((child) => {
    if (child.nodeType === Node.TEXT_NODE) {
      const t = cleanLine(child.textContent.replace(/\u00a0/g, ' '));
      if (t) blocks.push(t);
      return;
    }
    if (child.nodeType !== Node.ELEMENT_NODE) return;
    const tag = child.tagName.toLowerCase();
    if (SKIP_TAGS.includes(tag)) return;

    if (tag === 'h1' || tag === 'h2') {
      const t = cleanLine(inlineText(child).replace(/\*\*/g, ''));
      if (t) blocks.push(`## ${t}`);
    } else if (/^h[3-6]$/.test(tag)) {
      const t = cleanLine(inlineText(child).replace(/\*\*/g, ''));
      if (t) blocks.push(`### ${t}`);
    } else if (tag === 'ul' || tag === 'ol') {
      const items = Array.from(child.children)
        .filter((el) => el.tagName.toLowerCase() === 'li')
        .map((li) => cleanLine(inlineText(li)))
        .filter(Boolean)
        .map((t) => `• ${t.replace(/^[•\-*]\s*/, '')}`);
      if (items.length) blocks.push(items.join('\n'));
    } else if (tag === 'li') {
      const t = cleanLine(inlineText(child));
      if (t) blocks.push(`• ${t.replace(/^[•\-*]\s*/, '')}`);
    } else if (['p', 'div', 'section', 'article', 'main', 'blockquote', 'td', 'span'].includes(tag)) {
      if (child.querySelector('p, ul, ol, li, h1, h2, h3, h4, h5, h6, div, blockquote')) {
        blockify(child, blocks);
      } else {
        // paragraph with <br> line breaks: keep the breaks as separate blocks
        inlineText(child).split('\n').forEach((line) => {
          const t = cleanLine(line);
          if (t) blocks.push(t);
        });
      }
    } else if (tag === 'table' || tag === 'tbody' || tag === 'tr') {
      blockify(child, blocks);
    } else {
      const t = cleanLine(inlineText(child));
      if (t) blocks.push(t);
    }
  });
};

export const htmlToEditorMarkdown = (html) => {
  try {
    const doc = new DOMParser().parseFromString(html, 'text/html');
    const blocks = [];
    blockify(doc.body, blocks);
    // merge consecutive bullet blocks into a single list block
    const merged = [];
    blocks.forEach((b) => {
      const isBullets = b.split('\n').every((l) => l.startsWith('• '));
      const prev = merged[merged.length - 1];
      if (isBullets && prev && prev.split('\n').every((l) => l.startsWith('• '))) {
        merged[merged.length - 1] = `${prev}\n${b}`;
      } else {
        merged.push(b);
      }
    });
    return merged.join('\n\n').trim();
  } catch (_) {
    return '';
  }
};

export const normalizePlainText = (text) => {
  const t = (text || '').replace(/\r\n?/g, '\n').replace(/\u00a0/g, ' ');
  if (/\n{2,}/.test(t)) return t.replace(/\n{3,}/g, '\n\n').trim();
  // No blank lines at all: each line becomes its own paragraph,
  // except consecutive bullet lines which stay together as one list.
  const lines = t.split('\n').map((l) => l.trim());
  const blocks = [];
  let bullets = [];
  const flush = () => {
    if (bullets.length) { blocks.push(bullets.join('\n')); bullets = []; }
  };
  lines.forEach((line) => {
    if (!line) { flush(); return; }
    if (/^[•\-*]\s+/.test(line)) bullets.push(line.replace(/^[•\-*]\s+/, '• '));
    else { flush(); blocks.push(line); }
  });
  flush();
  return blocks.join('\n\n').trim();
};

// Main entry: returns the converted text, or '' if nothing usable.
export const clipboardToEditorMarkdown = (clipboardData) => {
  if (!clipboardData) return '';
  const html = clipboardData.getData('text/html');
  if (html && /<(p|h[1-6]|ul|ol|li|br|div|strong|b)\b/i.test(html)) {
    const converted = htmlToEditorMarkdown(html);
    if (converted) return converted;
  }
  return normalizePlainText(clipboardData.getData('text/plain'));
};
